import { PlasmicComponent } from '@plasmicapp/loader-nextjs';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import FaqCard from 'components/FaqCard';
import RoundedButton from 'components/RoundedButton';
import PlainTabControl from 'components/tab-control/PlainTabControl';
import { commonFaqs } from 'constants/FAQs';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import React, { ReactElement } from 'react';
import { ASSET_PREFIX, PRIZE_NUMBERS } from 'utils/constants';
import Celebration30 from '../components/svg/Celebration30.svg';
import Gift30 from '../components/svg/Gift30.svg';
import PoundInCircle30 from '../components/svg/PoundInCircle30.svg';
import Target30 from '../components/svg/Target30.svg';
import TickShield30 from '../components/svg/TickShield30.svg';
import Tree30 from '../components/svg/Tree30.svg';
import TrustStar30 from '../components/svg/TrustStar30.svg';
import styles from './choose-your-plan-v1.module.css';
import MainLayout from 'components/layout/MainLayout';
import { WinnerCarousel } from 'components/home-page/winner-section/WinnerCarousel';
import { GetServerSideProps } from 'next';
import { fetchWinnersHomepage, WinnerHomepage } from 'utils/plasmicCMS';
import { useEnterStore } from 'hooks/stores/useEnterStore';

interface Props {
  winners: WinnerHomepage[];
}

export const ChooseYourPlanV1 = (props: Props) => {
  const enterStore = useEnterStore();
  const heroImageDesktop =
    ASSET_PREFIX + '/images/choose-your-plan/2025/HeroV1-desktop.jpg';
  const heroImageMobile =
    ASSET_PREFIX + '/images/choose-your-plan/2025/HeroV1-mobile.jpg';

  return (
    <div className="aboutUsTypographyReset">
      <AboutUsModule background="white" withNoTopPadding withReducedPadding>
        <div
          className={styles.hero}
          style={{
            // See comment in choose-your-plan-module.css
            visibility: 'hidden',
          }}
        >
          {/* Use picture and source to decide which image to show on desktop vs mobile. We must ensure that the img element has width and height
          attributes, so that the browser knows what aspect ratio the image will be. The images on mobile vs desktop have different aspect ratios
          therefore there are two separate picture elements. The media queries that target the class decide which element to show */}
          <picture className={styles.heroImageDesktop}>
            <source
              srcSet="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
              media="(max-width: 767px)"
            />
            <source srcSet={heroImageDesktop} media="(min-width: 768px)" />
            <img src="" alt="Play YOUR way!" width="2496" height="1504" />
          </picture>
          <picture className={styles.heroImageMobile}>
            <source srcSet={heroImageMobile} media="(max-width: 767px)" />
            <source
              srcSet="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
              media="(min-width: 768px)"
            />
            <img src="" alt="Play YOUR way!" width="1560" height="844" />
          </picture>
          <AboutUsSiteContainer>
            <div className={styles.heroTrustBulletContainer}>
              <ul
                className={
                  styles.trustBulletList + ' aboutUsTypographyBodySmall'
                }
              >
                <li className={styles.trustBulletListItem}>
                  <CircleWithDropShadowIcon icon={Target30} />
                  <span>Over £11 million in prizes won</span>
                </li>
                <li className={styles.trustBulletListItem}>
                  <CircleWithDropShadowIcon icon={Celebration30} />
                  <span>Hundreds of prizes to choose from</span>
                </li>
                <li className={styles.trustBulletListItem}>
                  <CircleWithDropShadowIcon icon={Gift30} />
                  <span>Win more often, prizes big & small</span>
                </li>
                <li className={styles.trustBulletListItem}>
                  <CircleWithDropShadowIcon icon={Tree30} />
                  <span>Plant a tree with every purchase</span>
                </li>
              </ul>
            </div>
          </AboutUsSiteContainer>
        </div>
      </AboutUsModule>
      <AboutUsModule background="white" withReducedPadding>
        <AboutUsSiteContainer>
          <h2 className="visuallyHidden">Entry methods</h2>
          <PlainTabControl
            pages={[
              {
                title: 'By Post',
                content: (
                  <div className={styles.entryMethodTabPage}>
                    <h3 className="visuallyHidden">By Post</h3>
                    <p>
                      DAYMADE offers an alternative means of entering in to
                      prize draws. The process for using this alternative means
                      of entry is described in detail below. Just like the
                      standard way of entering the prize draw, this alternative
                      method requires you to accept all our terms and conditions
                      as well as require you to be older than 18 years old. On a
                      blank sheet of paper, write your name, address, date of
                      birth, email address and which prize draw you want to
                      enter (e.g. if you want to enter the Pound Raffle, please
                      specify 'Pound Raffle' OR if you want to enter a Weekly
                      Draw, please specify 'Weekly Draw Tuesday' or 'Weekly Draw
                      Friday'). If you don't specify which Weekly Draw you would
                      like to be entered for, then you will be entered into the
                      Friday draw. If you want to boost your Weekly Draw entry,
                      then write "boost" on your postal entry. This all needs to
                      be written down in very clear writing otherwise the entry
                      will be deemed invalid. Send the sheet of paper in an
                      envelope to the following address via either first or
                      second class post:
                    </p>
                    <p>
                      DAYMADE LTD
                      <br />
                      66 Paul Street
                      <br />
                      London
                      <br />
                      EC2A 4NA
                      <br />
                    </p>
                    <p>
                      A maximum of one entry can be made per postcard or
                      envelope received. The entry can only be applied to one
                      prize draw. As per our Terms & Conditions, you may only
                      enter up to four postal/paid entries per Weekly Draw. Any
                      additional postal entries sent after hitting this limit
                      will be discarded. Please note that, in the case where you
                      don't specify a prize draw OR if you include more than one
                      prize draw, you will (only) be entered into our Weekly
                      Draw on Friday. Entries received by post will be processed
                      within ten working days. Once a postal entry has been
                      processed it will be emailed to you. The email will
                      include your numbers. If you have entered a Weekly Draw,
                      your numbers will be chosen via a random number generator
                      (you cannot pick your own numbers). Once a postal entry
                      has been processed it will be entered in to the next
                      available draw for which the cut-off time has not yet
                      passed.
                    </p>
                    <p>
                      Weekly postal entrants will be considered subscribers and
                      will benefit from all the same perks as paying
                      subscribers. Postal entries are treated as exactly the
                      same as paid entries for the purposes of determining a
                      winner, and have an equal chance of winning as any paid
                      entries. If a postal entry wins a prize there will be no
                      further purchase or payment necessary to be notified of
                      the win or to receive the prize. If the above steps are
                      not followed as described then a postal entry will be
                      invalid. You will not be notified if your entry is
                      invalid.
                    </p>
                    <p>
                      Due to the rapid nature of the Pound Raffle and Token Town
                      raffles (with prizes changing very frequently), if you
                      send a postal entry and that turns out to be the winning
                      entry, you get given the option to choose from all
                      historical prizes of that specific raffle.
                    </p>
                  </div>
                ),
              },
              {
                title: 'Online',
                content: (
                  <div className={styles.entryMethodTabPage}>
                    <h3 className="visuallyHidden">Online</h3>
                    <div className={styles.entryMethodCards}>
                      <div
                        className={
                          styles.entryMethodCard + ' withMinimalSpacing'
                        }
                      >
                        <div className={styles.entryMethodContent}>
                          <div className={styles.entryMethodHeading}>
                            <h4 className="aboutUsTypographyHeaderLarge">
                              Boost
                            </h4>
                            <span>from £3.45/ week</span>
                          </div>
                          <p>Unlock our best prizes and odds</p>
                          <ul
                            className={
                              styles.entryMethodList +
                              ' aboutUsTypographyBodySmall'
                            }
                          >
                            <li>
                              Chance to win prizes worth{' '}
                              <strong>up to £100k</strong>
                            </li>
                            <li>
                              <strong>8x</strong> better odds than Lite*
                            </li>
                            <li>
                              Cancel <strong>anytime</strong>
                            </li>
                          </ul>
                          <div className={styles.entryMethodSticker}>
                            <div className={styles.entryMethodStickerText}>
                              <strong>50% off</strong> 1st entry
                            </div>
                          </div>
                          <span className={styles.entryMethodBadge}>
                            MOST POPULAR
                          </span>
                        </div>
                        <RoundedButton
                          href="/enternow/1"
                          rebrandDm2025
                          fullWidth
                          onClick={() => {
                            enterStore.setBoosted(true);
                          }}
                        >
                          Get Boost
                        </RoundedButton>
                      </div>
                      <div
                        className={
                          styles.entryMethodCard + ' withMinimalSpacing'
                        }
                      >
                        <div className={styles.entryMethodContent}>
                          <div className={styles.entryMethodHeading}>
                            <h4 className="aboutUsTypographyHeaderLarge">
                              Premium
                            </h4>
                            <span>from £2.95/ week</span>
                          </div>
                          <p>Unlock our best odds</p>
                          <ul
                            className={
                              styles.entryMethodList +
                              ' aboutUsTypographyBodySmall'
                            }
                          >
                            <li>
                              Chance to win prizes worth{' '}
                              <strong>up to £25k</strong>
                            </li>
                            <li>
                              <strong>8x</strong> better odds than Lite*
                            </li>
                            <li>
                              Cancel <strong>anytime</strong>
                            </li>
                          </ul>
                        </div>
                        <RoundedButton
                          href="/enternow/1"
                          rebrandDm2025
                          fullWidth
                          onClick={() => {
                            enterStore.setBoosted(false);
                          }}
                        >
                          Get Premium
                        </RoundedButton>
                      </div>
                      <div
                        className={
                          styles.entryMethodCard + ' withMinimalSpacing'
                        }
                      >
                        <div className={styles.entryMethodContent}>
                          <div className={styles.entryMethodHeading}>
                            <h4 className="aboutUsTypographyHeaderLarge">
                              Lite
                            </h4>
                            <span>from £1.50/ week</span>
                          </div>
                          <p>Fewer prizes and tougher odds</p>
                          <ul
                            className={
                              styles.entryMethodList +
                              ' aboutUsTypographyBodySmall'
                            }
                          >
                            <li>
                              Chance to win prizes worth{' '}
                              <strong>up to £10k</strong>
                            </li>
                            <li>
                              Cancel <strong>anytime</strong>
                            </li>
                          </ul>
                        </div>
                        <RoundedButton
                          href="/enter?em=LiteSubscription"
                          rebrandDm2025
                          fullWidth
                        >
                          Get Lite
                        </RoundedButton>
                      </div>
                    </div>
                    <div className={styles.entryMethodAllPlans}>
                      <h3 className="aboutUsTypographyHeaderMedium">
                        All plans include
                      </h3>
                      <ul className={styles.entryMethodAllPlansList}>
                        <li>
                          <strong>Cancel anytime</strong> - you’re in control
                        </li>
                        <li>
                          Automatic entry - <strong>never miss a draw</strong>
                        </li>
                        <li>
                          <strong>1 in 3</strong> chance to win our smallest
                          prizes
                        </li>
                        <li>Choose your prize when you win</li>
                        <li>
                          <strong>Exclusive</strong> member raffles & perks
                        </li>
                        <li>One tree planted per draw you enter</li>
                      </ul>
                      <hr className={styles.horizontalRule} />
                      <div className="withReducedSpacing">
                        <p className="aboutUsTypographyCaption aboutUsTypographyCentered">
                          *Valid for Bronze prizes and above (excluding Boost)
                        </p>
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
            initialActiveIndex={1}
          ></PlainTabControl>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <AboutUsModule background="grey" withReducedPadding>
        <AboutUsSiteContainer>
          <h2
            className={
              'aboutUsTypographyHeaderXlargeOnWhite' +
              ' ' +
              styles.prizePlatformTitle
            }
            data-text="A prize platform you can trust"
          >
            A prize platform you can trust
          </h2>
          <div className={styles.prizePlatformBulletContainer}>
            <ul
              className={
                styles.trustBulletList +
                ' ' +
                styles.trustBulletListQuarterOnMobile +
                ' ' +
                styles.trustBulletListStackOnMobile
              }
            >
              <li className={styles.trustBulletListItem}>
                <CircleWithDropShadowIcon icon={TrustStar30} />
                <span>4.5★ rating on Trustpilot</span>
              </li>
              <li className={styles.trustBulletListItem}>
                <CircleWithDropShadowIcon icon={Tree30} />
                <span>5m trees planted</span>
              </li>
              <li className={styles.trustBulletListItem}>
                <CircleWithDropShadowIcon icon={PoundInCircle30} />
                <span>
                  {/* {!drawStore.prizesWonValue || isNaN(drawStore.prizesWonValue)
                    ? 'Lots of'
                    : '£' +
                    (drawStore.prizesWonValue / 10000000000)
                      .toFixed(1)
                      .replace('.0', '') +
                    'm'}{' '} */}
                  £11m prizes already won
                </span>
              </li>
              <li className={styles.trustBulletListItem}>
                <CircleWithDropShadowIcon icon={TickShield30} />
                <span>Registered with Fundraising Regulator</span>
              </li>
            </ul>
          </div>
          <div
            /* Need overflow hidden, because the plasmic component spills out of its container */
            style={{ overflow: 'hidden' }}
          >
            <div>
              <WinnerCarousel winners={props.winners} />
            </div>
          </div>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <AboutUsModule background="white" withReducedPadding>
        <AboutUsSiteContainer>
          <h2 className="aboutUsTypographyHeaderXlargeOnWhite" data-text="FAQ">
            FAQ
          </h2>
          {faqs.map((x, index) => (
            <FaqCard
              key={index}
              rebrand2025
              question={x.question}
              answer={x.answer}
              withBorder
            ></FaqCard>
          ))}
        </AboutUsSiteContainer>
      </AboutUsModule>
    </div>
  );
};

interface Faq {
  question: string;
  answer: ReactElement;
}

const faqs: Faq[] = [
  commonFaqs.howDoesDaymadeWork,
  {
    question: 'How are there so many winners?',
    answer: (
      <p>
        With DAYMADE, you only need to match 1 number to start winning. Every
        week, we have over thousands of winners of prizes big and small: from
        tokens and treats to bucketlist holidays, home makeovers, and lifestyle
        prizes. Of course the odds of matching all 5 numbers are a lot worse
        than matching 1 or 2 numbers. While we've had a lot of lucky winners
        winning dream holidays, gadget bundles, luxury items & more, most of our
        winners win the smaller prizes like tokens and Bronze prizes. We try to
        make as many people’s day as we can. Match 1 number to win a token and
        match all 5 numbers to claim that amazing holiday or home makeover worth
        up to £100,000.
      </p>
    ),
  },
  {
    question: 'Can I buy a one-off ticket?',
    answer: (
      <p>
        For online entries, DAYMADE is a subscription that can be snoozed or
        cancelled any time. You can enter one-off via our Postal Entry Route.
      </p>
    ),
  },
  {
    question: 'What prizes can I win?',
    answer: (
      <p>
        You can win a diverse range of prizes, from tokens to holidays,
        experiences, lifestyle items, tech gadgets, and family-oriented prizes
        in our weekly draws. We have over {PRIZE_NUMBERS.PREMIUM} prizes on
        offer that are hand selected by our team, so there’s something for
        everyone.
      </p>
    ),
  },
  {
    question: 'Can I choose my prize?',
    answer: (
      <p>
        Yes! With over {PRIZE_NUMBERS.PREMIUM} prizes to choose from, there’s
        something for everyone. For each number you match correctly, you will
        unlock a larger prize tier. Our tiers include Bronze, Silver, Gold,
        Platinum, Diamond, and for our grand prize, the DAYMAKER tier. Each tier
        has a wide assortment of prizes to choose from.
      </p>
    ),
  },
  {
    question: 'How will I be notified when I win?',
    answer: (
      <p>
        We email all winners within two hours of the draw. If we don’t hear from
        you within 2 weeks, don’t worry, we will send you a reminder. You can
        also log-in to your account at any time and visit your account page to
        view any prizes that you have won.
      </p>
    ),
  },
  {
    question: 'What are tokens?',
    answer: (
      <p>
        Tokens are our smallest prize, which you can win by matching just 1
        number. You can then use these tokens to plant more trees or enter more
        prize draws on our site. Some people save up tokens over time in order
        to redeem a bigger prize.
      </p>
    ),
  },
  {
    question: 'Can I customise my holiday and bring a guest(s)?',
    answer: (
      <p>
        Definitely! Most trips are planned for 2 people, but we can tailor the
        number of travellers to suit your needs, as long as it fits within the
        prize budget. We also include unique activities and experiences to make
        your trip unforgettable. Just pick your dates, and we'll handle the
        rest. You can find out what's included in your chosen trip by checking
        the prize details.
      </p>
    ),
  },
];

const CircleWithDropShadowIcon = (props: { icon: React.ComponentType }) => {
  const IconComponent = props.icon;
  return (
    <span className={styles.circleWithDropShadow}>
      <span className={styles.circleWithDropShadowInner}>
        <IconComponent />
        <span className={styles.circleWithDropShadowAlt}>
          <IconComponent />
        </span>
      </span>
    </span>
  );
};

const DummyComponent = () => <div></div>;
export default DummyComponent;
export async function getServerSideProps() {
  return {
    // This AB test page shouldn't serve content. If the winner is chosen, then we rename this page to choose-your-plan
    notFound: true,
  };
}
